import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import {
  CERAMICS_QUERY,
  CERAMICS_COUNT_QUERY,
  PUBLICATIONS_QUERY,
  SITES_QUERY,
  TAGS_QUERY
} from '../graphql/queries';
import FilterDialog from '../components/FilterDialog';
import usePaginatedList from '../hooks/usePaginatedList';
import PaginationControls from '../components/PaginationControls';
import DataTable from '../components/DataTable';
import QueryError from '../components/QueryError';
import M3LinearProgress from '../components/M3Progress';
import M3Chip from '../components/M3Chip';
import TableActions from '../components/TableActions';

export default function CeramicsPage() {
  const [filterOpen, setFilterOpen] = useState(false);

  const { data: publicationsData } = useQuery(PUBLICATIONS_QUERY);
  const publications =
    publicationsData?.publications?.map((p) => ({ id: p.id, label: p.title })) || [];

  const { data: sitesData } = useQuery(SITES_QUERY);
  const sites = sitesData?.sites || [];

  const { data: tagsData } = useQuery(TAGS_QUERY);
  const tags = tagsData?.tags || [];

  const {
    filters,
    page,
    setPage,
    sort,
    setSort,
    listData,
    loading,
    error,
    total,
    totalPages,
    refetch
  } = usePaginatedList(CERAMICS_QUERY, CERAMICS_COUNT_QUERY, { baseVars: {} });

  const handleSort = (columnKey) => {
    const [key, direction] = sort.split(':');
    const newDirection = key === columnKey && direction === 'asc' ? 'desc' : 'asc';
    setSort(`${columnKey}:${newDirection}`);
  };

  const handleFilterApply = (newFilters) => {
    const vars = {};
    for (const [key, value] of Object.entries(newFilters)) {
      if (key === 'tagIds') {
        vars[key] = value.map((v) => parseInt(v, 10));
      } else if (key === 'verified') {
        vars[key] = value === 'true' ? true : value === 'false' ? false : null;
      } else if (value) {
        vars[key] = parseInt(value, 10);
      }
    }
    refetch(vars);
  };

  const columns = [
    { key: 'identifier', title: 'ID', numeric: true },
    {
      key: 'similarities',
      title: 'Similarities',
      render: (v, row) => (
        <Link to={`/ceramics/${row.id}/similarities`}>Compare</Link>
      )
    },
    {
      key: 'width',
      title: 'Width',
      numeric: true,
      render: (v) => (v ? v.toFixed(0) : '—')
    },
    {
      key: 'height',
      title: 'Length',
      numeric: true,
      render: (v) => (v ? v.toFixed(0) : '—')
    },
    {
      key: 'publication',
      title: 'Publication',
      render: (pub) => pub?.title || '—'
    },
    {
      key: 'site',
      title: 'Site',
      render: (s) => s?.name || '—'
    },
    {
      key: 'verified',
      title: 'Verified',
      render: (v) => (v ? '✓' : '—')
    },
    {
      key: 'tags',
      title: 'Tags',
      render: (tags) => tags?.map((t) => t.name).join(', ') || '—'
    }
  ];

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== '' && v !== null && v !== undefined
  ).length;

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Ceramics</h1>
        <div className="d-flex align-items-center gap-2">
          {activeFilterCount > 0 && (
            <M3Chip variant="filter" selected>
              {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </M3Chip>
          )}
          <button className="btn btn-secondary" onClick={() => setFilterOpen(true)}>
            Filters
          </button>
        </div>
      </div>

      {loading && <M3LinearProgress indeterminate />}

      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleFilterApply}
        filters={filters}
        publications={publications}
        sites={sites}
        tags={tags}
      />

      <DataTable
        columns={columns}
        data={listData}
        loading={loading}
        rowKey="id"
        emptyMessage="No ceramics found"
        sortConfig={{ key: sort.split(':')[0], direction: sort.split(':')[1] }}
        onSort={handleSort}
        actions={(ceramic) => (
          <TableActions
            items={[{ icon: 'edit', label: 'Edit', to: `/figures/ceramics/${ceramic.id}/edit` }]}
          />
        )}
      />
      <PaginationControls
        page={page}
        totalPages={totalPages}
        total={total}
        totalLabel="ceramic"
        setPage={setPage}
      />
    </div>
  );
}
